import React, { useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon, ListTreeIcon, XIcon } from 'lucide-react';
import { FormSection, TextField, SelectField, TextAreaField, DisplayField, FileUploadField } from '../components/FormComponents';
import { mockReceives } from '../data/mockData';
import { productHierarchy } from './ProductForm';
// Mock PO phases data
const poPhasesData: Record<string, any[]> = {
  'Phase 1': [{
    id: 'p1-1',
    type: 'Goods',
    product: 'Laptop Pro X',
    description: 'High performance laptops for dev team',
    orderedQty: 3,
    previouslyReceivedQty: 0,
    receivingQty: 3,
    pendingQty: 0,
    rate: 1200,
    lineAmount: 3600
  }, {
    id: 'p1-2',
    type: 'Service',
    product: 'IT Setup Service',
    description: 'Initial configuration and setup',
    orderedQty: 1,
    previouslyReceivedQty: 0,
    receivingQty: 1,
    pendingQty: 0,
    rate: 500,
    lineAmount: 500
  }],
  'Phase 2': [{
    id: 'p2-1',
    type: 'Goods',
    product: 'Laptop Pro X',
    description: 'High performance laptops for dev team',
    orderedQty: 2,
    previouslyReceivedQty: 0,
    receivingQty: 2,
    pendingQty: 0,
    rate: 1200,
    lineAmount: 2400
  }, {
    id: 'p2-2',
    type: 'Works',
    product: 'Network Cabling',
    description: 'Office floor network installation',
    orderedQty: 1,
    previouslyReceivedQty: 0,
    receivingQty: 1,
    pendingQty: 0,
    rate: 1500,
    lineAmount: 1500
  }]
};
export function ReceiveForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const mode = id ? 'edit' : 'create';
  const initialData = mode === 'edit' && id ? mockReceives.find(r => r.id === id) : null;
  const [formData, setFormData] = useState<any>(initialData || {
    receiveNo: 'RCV-AUTO-GEN',
    linkedPONo: '',
    deliveryPhase: '',
    vendor: '',
    department: '',
    project: '',
    procurementMethod: '',
    receiveDate: new Date().toISOString().split('T')[0],
    deliveryChallanNo: '',
    remarks: ''
  });
  const [lineItems, setLineItems] = useState<any[]>(initialData?.lineItems?.map(item => ({
    ...item,
    type: item.type || 'Goods',
    description: item.description || ''
  })) || []);
  const [additionalItems, setAdditionalItems] = useState<any[]>([]);
  // Breakdown state
  const [breakdowns, setBreakdowns] = useState<Record<string, any[]>>({});
  const [activeBreakdownItemId, setActiveBreakdownItemId] = useState<string | null>(null);
  const [tempBreakdown, setTempBreakdown] = useState<any[]>([]);
  const handlePhaseChange = (phase: string) => {
    setFormData({
      ...formData,
      deliveryPhase: phase
    });
    if (phase && poPhasesData[phase]) {
      setLineItems(poPhasesData[phase]);
      setBreakdowns({});
    } else {
      setLineItems([]);
      setBreakdowns({});
    }
  };
  const handleLineItemChange = (id: string, field: string, value: any) => {
    setLineItems(items => items.map(item => {
      if (item.id === id) {
        const updated = {
          ...item,
          [field]: value
        };
        if (field === 'receivingQty') {
          updated.pendingQty = updated.orderedQty - updated.previouslyReceivedQty - updated.receivingQty;
          updated.lineAmount = updated.receivingQty * updated.rate;
        }
        return updated;
      }
      return item;
    }));
  };
  const handleAdditionalItemChange = (id: string, field: string, value: any) => {
    setAdditionalItems(items => items.map(item => {
      if (item.id === id) {
        const updated = {
          ...item,
          [field]: value
        };
        if (field === 'receivingQty' || field === 'rate') {
          updated.lineAmount = (Number(updated.receivingQty) || 0) * (Number(updated.rate) || 0);
        }
        return updated;
      }
      return item;
    }));
  };
  const addAdditionalItem = () => {
    setAdditionalItems([...additionalItems, {
      id: `add-${Date.now()}`,
      type: 'Goods',
      product: '',
      description: '',
      orderedQty: 0,
      previouslyReceivedQty: 0,
      receivingQty: 1,
      pendingQty: 0,
      rate: 0,
      lineAmount: 0
    }]);
  };
  const removeAdditionalItem = (id: string) => {
    setAdditionalItems(additionalItems.filter(item => item.id !== id));
  };
  // Breakdown Modal Functions
  const openBreakdownModal = (itemId: string) => {
    setActiveBreakdownItemId(itemId);
    setTempBreakdown(breakdowns[itemId] ? [...breakdowns[itemId]] : []);
  };
  const closeBreakdownModal = () => {
    setActiveBreakdownItemId(null);
    setTempBreakdown([]);
  };
  const addBreakdownRow = () => {
    setTempBreakdown([...tempBreakdown, {
      id: `bd-${Date.now()}`,
      subProduct: '',
      item: '',
      subItem: '',
      qty: 1
    }]);
  };
  const removeBreakdownRow = (id: string) => {
    setTempBreakdown(tempBreakdown.filter(row => row.id !== id));
  };
  const handleBreakdownChange = (id: string, field: string, value: any) => {
    setTempBreakdown(rows => rows.map(row => {
      if (row.id === id) {
        const updated = {
          ...row,
          [field]: value
        };
        if (field === 'subProduct') {
          updated.item = '';
          updated.subItem = '';
        } else if (field === 'item') {
          updated.subItem = '';
        }
        return updated;
      }
      return row;
    }));
  };
  const saveBreakdown = () => {
    if (!activeBreakdownItemId) return;
    const activeItem = lineItems.find(item => item.id === activeBreakdownItemId);
    if (!activeItem) return;
    const totalQty = tempBreakdown.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
    if (totalQty > activeItem.orderedQty) {
      alert(`Total breakdown quantity (${totalQty}) cannot exceed ordered quantity (${activeItem.orderedQty}).`);
      return;
    }
    setBreakdowns({
      ...breakdowns,
      [activeBreakdownItemId]: tempBreakdown
    });
    closeBreakdownModal();
  };
  const totalReceiveAmount = lineItems.reduce((sum, item) => sum + item.lineAmount, 0) + additionalItems.reduce((sum, item) => sum + item.lineAmount, 0);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Receive entry ${mode === 'create' ? 'created' : 'updated'} successfully!`);
    navigate('/receives');
  };
  const activeItemForModal = activeBreakdownItemId ? lineItems.find(i => i.id === activeBreakdownItemId) : null;
  const currentBreakdownTotal = tempBreakdown.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
  return <div className="space-y-6 max-w-6xl mx-auto" data-id="element-1824">
      <div className="flex items-center justify-between" data-id="element-1825">
        <div data-id="element-1826">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1827">
            {mode === 'create' ? 'Create Receive Entry' : 'Edit Receive Entry'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1828">
            Record goods, works, or services received for a single PO delivery
            phase.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-1829">
        <FormSection title="Basic Information" data-id="element-1830">
          <DisplayField label="Receive No" value={formData.receiveNo} data-id="element-1831" />

          {mode === 'edit' ? <DisplayField label="Linked PO No" value={formData.linkedPONo} data-id="element-1832" /> : <SelectField label="Linked PO No" name="linkedPONo" value={formData.linkedPONo} onChange={e => setFormData({
          ...formData,
          linkedPONo: e.target.value,
          vendor: 'TechCorp Solutions',
          department: 'IT-01',
          project: 'PRJ-IT-100',
          procurementMethod: 'Direct Purchase',
          deliveryPhase: ''
        })} required options={[{
          value: 'PO-2023-001',
          label: 'PO-2023-001'
        }]} data-id="element-1833" />}

          {formData.linkedPONo && <SelectField label="PO Delivery Phase" name="deliveryPhase" value={formData.deliveryPhase} onChange={e => handlePhaseChange(e.target.value)} required options={[{
          value: 'Phase 1',
          label: 'Phase 1 — Expected: 2023-11-14'
        }, {
          value: 'Phase 2',
          label: 'Phase 2 — Expected: 2023-12-01'
        }]} data-id="element-1834" />}

          {formData.linkedPONo && formData.deliveryPhase && <div className="md:col-span-2" data-id="element-1835">
              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-3 rounded-r-md" data-id="element-1836">
                <p className="text-sm text-indigo-700" data-id="element-1837">
                  This receive entry is for{' '}
                  <strong data-id="element-1838">{formData.deliveryPhase}</strong> of{' '}
                  <strong data-id="element-1839">{formData.linkedPONo}</strong>. Each delivery phase
                  requires a separate receive entry.
                </p>
              </div>
            </div>}

          <DisplayField label="Vendor" value={formData.vendor || '-'} data-id="element-1840" />
          <DisplayField label="Department" value={formData.department || '-'} data-id="element-1841" />
          <DisplayField label="Project" value={formData.project || '-'} data-id="element-1842" />
          <DisplayField label="Procurement Method" value={formData.procurementMethod || '-'} data-id="element-1843" />

          <TextField label="Receive Date" name="receiveDate" type="date" value={formData.receiveDate} onChange={e => setFormData({
          ...formData,
          receiveDate: e.target.value
        })} required data-id="element-1844" />
          <TextField label="Delivery Challan No" name="deliveryChallanNo" value={formData.deliveryChallanNo} onChange={e => setFormData({
          ...formData,
          deliveryChallanNo: e.target.value
        })} data-id="element-1845" />
        </FormSection>

        <div className="mb-8" data-id="element-1846">
          <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-1847">
            PO Line Items
          </h3>
          <div className="overflow-x-auto" data-id="element-1848">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-1849">
              <thead className="bg-gray-50" data-id="element-1850">
                <tr data-id="element-1851">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1852">
                    Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1853">
                    Product/Asset
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1854">
                    Description
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1855">
                    Ordered Qty
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1856">
                    Prev. Received
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1857">
                    Receiving Qty
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1858">
                    Pending Qty
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1859">
                    Rate
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1860">
                    Line Amount
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1861">
                    Breakdown
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1862">
                {lineItems.length === 0 ? <tr data-id="element-1863">
                    <td colSpan={10} className="px-4 py-4 text-center text-sm text-gray-500" data-id="element-1864">
                      Select a PO and Delivery Phase to load line items.
                    </td>
                  </tr> : lineItems.map(item => {
                const hasBreakdown = breakdowns[item.id] && breakdowns[item.id].length > 0;
                return <tr key={item.id} data-id="element-1865">
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1866">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.type === 'Goods' ? 'bg-blue-100 text-blue-800' : item.type === 'Works' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1867">
                            {item.type}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900" data-id="element-1868">
                          {item.product}
                          {hasBreakdown && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1869">
                              {breakdowns[item.id].length} sub-items
                            </span>}
                        </td>
                        <td className="px-4 py-2" data-id="element-1870">
                          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.description} onChange={e => handleLineItemChange(item.id, 'description', e.target.value)} placeholder="Add description..." data-id="element-1871" />
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1872">
                          {item.orderedQty}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1873">
                          {item.previouslyReceivedQty}
                        </td>
                        <td className="px-4 py-2" data-id="element-1874">
                          <input type="number" min="0" max={item.orderedQty - item.previouslyReceivedQty} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.receivingQty} onChange={e => handleLineItemChange(item.id, 'receivingQty', Number(e.target.value))} data-id="element-1875" />
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1876">
                          {item.pendingQty}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500" data-id="element-1877">
                          ${item.rate}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-1878">
                          ${item.lineAmount.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-center" data-id="element-1879">
                          <button type="button" onClick={() => openBreakdownModal(item.id)} className="inline-flex items-center justify-center p-1.5 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Sub-item breakdown" data-id="element-1880">
                            <ListTreeIcon className="h-4 w-4" data-id="element-1881" />
                          </button>
                        </td>
                      </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-8" data-id="element-1882">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200" data-id="element-1883">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-1884">
              Additional Item Receive
            </h3>
            <button type="button" onClick={addAdditionalItem} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200" data-id="element-1885">
              <PlusIcon className="h-3.5 w-3.5 mr-1" data-id="element-1886" /> Add Item
            </button>
          </div>

          {additionalItems.length > 0 ? <div className="overflow-x-auto" data-id="element-1887">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-1888">
                <thead className="bg-gray-50" data-id="element-1889">
                  <tr data-id="element-1890">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1891">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1892">
                      Product/Asset
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1893">
                      Description
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1894">
                      Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1895">
                      Rate
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1896">
                      Amount
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16" data-id="element-1897">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1898">
                  {additionalItems.map(item => <tr key={item.id} data-id="element-1899">
                      <td className="px-4 py-2" data-id="element-1900">
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.type} onChange={e => handleAdditionalItemChange(item.id, 'type', e.target.value)} data-id="element-1901">
                          <option value="Goods" data-id="element-1902">Goods</option>
                          <option value="Works" data-id="element-1903">Works</option>
                          <option value="Service" data-id="element-1904">Service</option>
                        </select>
                      </td>
                      <td className="px-4 py-2" data-id="element-1905">
                        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.product} onChange={e => handleAdditionalItemChange(item.id, 'product', e.target.value)} placeholder="Item name" data-id="element-1906" />
                      </td>
                      <td className="px-4 py-2" data-id="element-1907">
                        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.description} onChange={e => handleAdditionalItemChange(item.id, 'description', e.target.value)} placeholder="Description" data-id="element-1908" />
                      </td>
                      <td className="px-4 py-2" data-id="element-1909">
                        <input type="number" min="1" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.receivingQty} onChange={e => handleAdditionalItemChange(item.id, 'receivingQty', Number(e.target.value))} data-id="element-1910" />
                      </td>
                      <td className="px-4 py-2" data-id="element-1911">
                        <input type="number" min="0" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.rate} onChange={e => handleAdditionalItemChange(item.id, 'rate', Number(e.target.value))} data-id="element-1912" />
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-1913">
                        ${item.lineAmount.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-center" data-id="element-1914">
                        <button type="button" onClick={() => removeAdditionalItem(item.id)} className="text-red-500 hover:text-red-700" data-id="element-1915">
                          <TrashIcon className="h-4 w-4 mx-auto" data-id="element-1916" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <p className="text-sm text-gray-500 italic" data-id="element-1917">
              No additional items added.
            </p>}
        </div>

        <div className="mb-8 bg-gray-50 p-4 rounded-md border border-gray-200 flex justify-end items-center" data-id="element-1918">
          <span className="text-sm font-medium text-gray-700 mr-4" data-id="element-1919">
            Total Receive Amount:
          </span>
          <span className="text-xl font-bold text-indigo-600" data-id="element-1920">
            ${totalReceiveAmount.toLocaleString()}
          </span>
        </div>

        <FormSection title="Additional Details" data-id="element-1921">
          <TextAreaField label="Remarks" name="remarks" value={formData.remarks} onChange={e => setFormData({
          ...formData,
          remarks: e.target.value
        })} rows={3} data-id="element-1922" />
          <FileUploadField label="Attachments (Delivery Challan)" data-id="element-1923" />
        </FormSection>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1924">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1925">
            Cancel
          </button>
          <button type="button" className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100" data-id="element-1926">
            Save as Draft
          </button>
          <button type="submit" className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-1927">
            Submit Receive
          </button>
        </div>
      </form>

      {/* Breakdown Modal */}
      {activeBreakdownItemId && activeItemForModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1928">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0" data-id="element-1929">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-1930">
              <div className="absolute inset-0 bg-gray-500 opacity-75 backdrop-blur-sm" onClick={closeBreakdownModal} data-id="element-1931"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl w-full" data-id="element-1932">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-1933">
                <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-200" data-id="element-1934">
                  <div data-id="element-1935">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1936">
                      Item Breakdown — {activeItemForModal.product}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-1937">
                      Ordered Qty: {activeItemForModal.orderedQty} | Breakdown
                      Total:{' '}
                      <span className={currentBreakdownTotal > activeItemForModal.orderedQty ? 'text-red-600 font-bold' : 'text-green-600 font-bold'} data-id="element-1938">
                        {currentBreakdownTotal}
                      </span>
                    </p>
                  </div>
                  <button onClick={closeBreakdownModal} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-1939">
                    <XIcon className="h-6 w-6" data-id="element-1940" />
                  </button>
                </div>

                <div className="overflow-x-auto" data-id="element-1941">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-1942">
                    <thead className="bg-gray-50" data-id="element-1943">
                      <tr data-id="element-1944">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1945">
                          Sub Product
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1946">
                          Item
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1947">
                          Sub Item
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1948">
                          Qty
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16" data-id="element-1949">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-1950">
                      {tempBreakdown.length === 0 ? <tr data-id="element-1951">
                          <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500" data-id="element-1952">
                            No breakdown items added yet. Click "Add Row" to
                            begin.
                          </td>
                        </tr> : tempBreakdown.map(row => {
                    // Get available options based on hierarchy
                    // We'll use all subProducts across all products for the first dropdown since we don't know the parent Product here
                    const allSubProducts = Object.values(productHierarchy.subProducts).flat();
                    const availableItems = row.subProduct ? productHierarchy.items[row.subProduct] || [] : [];
                    const availableSubItems = row.item ? productHierarchy.subItems[row.item] || [] : [];
                    return <tr key={row.id} data-id="element-1953">
                              <td className="px-4 py-2" data-id="element-1954">
                                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 bg-white" value={row.subProduct} onChange={e => handleBreakdownChange(row.id, 'subProduct', e.target.value)} data-id="element-1955">
                                  <option value="" data-id="element-1956">
                                    Select Sub Product...
                                  </option>
                                  {allSubProducts.map(sp => <option key={sp} value={sp} data-id="element-1957">
                                      {sp}
                                    </option>)}
                                </select>
                              </td>
                              <td className="px-4 py-2" data-id="element-1958">
                                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 bg-white" value={row.item} onChange={e => handleBreakdownChange(row.id, 'item', e.target.value)} disabled={!row.subProduct} data-id="element-1959">
                                  <option value="" data-id="element-1960">Select Item...</option>
                                  {availableItems.map(i => <option key={i} value={i} data-id="element-1961">
                                      {i}
                                    </option>)}
                                </select>
                              </td>
                              <td className="px-4 py-2" data-id="element-1962">
                                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 bg-white" value={row.subItem} onChange={e => handleBreakdownChange(row.id, 'subItem', e.target.value)} disabled={!row.item} data-id="element-1963">
                                  <option value="" data-id="element-1964">Select Sub Item...</option>
                                  {availableSubItems.map(si => <option key={si} value={si} data-id="element-1965">
                                      {si}
                                    </option>)}
                                </select>
                              </td>
                              <td className="px-4 py-2" data-id="element-1966">
                                <input type="number" min="1" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={row.qty} onChange={e => handleBreakdownChange(row.id, 'qty', Number(e.target.value))} data-id="element-1967" />
                              </td>
                              <td className="px-4 py-2 text-center" data-id="element-1968">
                                <button type="button" onClick={() => removeBreakdownRow(row.id)} className="text-red-500 hover:text-red-700" data-id="element-1969">
                                  <TrashIcon className="h-4 w-4 mx-auto" data-id="element-1970" />
                                </button>
                              </td>
                            </tr>;
                  })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex justify-between items-center" data-id="element-1971">
                  <button type="button" onClick={addBreakdownRow} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200" data-id="element-1972">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-1973" /> Add Row
                  </button>

                  {currentBreakdownTotal > activeItemForModal.orderedQty && <span className="text-sm text-red-600 font-medium" data-id="element-1974">
                      Total quantity exceeds ordered quantity!
                    </span>}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200" data-id="element-1975">
                <button type="button" onClick={saveBreakdown} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-1976">
                  Save Breakdown
                </button>
                <button type="button" onClick={closeBreakdownModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-1977">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}